import CumulativeRatings from "@src/views/brands/dashboard/CumulativeRatings";
import RecommendedRDs from "@src/views/brands/dashboard/RecommendedRDs";
import RecommendedOpportunities from "@src/views/brands/dashboard/RecommendedOpportunities";

import "@styles/react/libs/charts/apex-charts.scss";
import "@styles/react/libs/charts/recharts.scss";
import "@styles/react/libs/flatpickr/flatpickr.scss";
import "@styles/react/libs/swiper/swiper.scss";
import { useEffect, useState } from "react";
import { useHistory } from "react-router";
import {
  Alert,
  Button,
  Card,
  CardBody,
  Col,
  Form,
  FormGroup,
  Input,
  Modal,
  ModalBody,
  ModalHeader,
  Row,
  Spinner,
} from "reactstrap";
import SwiperCore, {
  Autoplay,
  EffectCoverflow,
  EffectCube,
  EffectFade,
  Lazy,
  Navigation,
  Pagination,
  Virtual,
} from "swiper";
import { useDispatch, useSelector } from "react-redux";
import {
  sendVerifyEmailLinkAction,
  sendVerifyEmailLinkResetAction,
} from "@src/views/apps/opportunitiesBrands/store/actions";
import {
  verifyEmailAction,
  verifyEmailResetAction,
} from "@src/views/pages/emailVerification/store/actions";
import { getBrandsSubscriptionPlans } from "@src/redux/actions/brandProfile/index";
import { AlertCircle, CheckCircle, Award, X, RefreshCw } from "react-feather";
import UpgradeAnnualModal from "./UpgradeAnnualModal";
import RequestInvoiceModal from "../../outerPages/SubscriptionPlans/RequestInvoiceModal";
import BrandAnnouncement from "./BrandAnnouncement";
import NextSteps from "./NextSteps";
import YourPrograms from "./YourPrograms";
import RecentApplicants from "./RecentApplicants";
import MembershipRenewalSummary from "./MembershipRenewalSummary";
import SubscriptionExpiryBanner from "@src/views/brands/components/SubscriptionExpiryBanner";
import { getDashboardGuidance } from "@src/redux/actions/brandsDashboard";
import "./dashboardGuidance.scss";
import { Formik } from "formik";
import * as yup from "yup";

SwiperCore.use([
  Navigation,
  Pagination,
  EffectFade,
  EffectCube,
  EffectCoverflow,
  Autoplay,
  Lazy,
  Virtual,
]);

const BrandDashboard = () => {
  const history = useHistory();
  const dispatch = useDispatch();

  const [openModal, setOpenModal] = useState(false);
  const [upgradeModalOpen, setUpgradeModalOpen] = useState(false);
  const [invoiceModalOpen, setInvoiceModalOpen] = useState(false);

  const data = JSON.parse(localStorage.getItem(process.env.REACT_APP_USERDATA));

  const { currentPlanData } = useSelector((store) => store.brandDashboard);

  const { dashboardGuidanceData, dashboardGuidanceLoading, dashboardGuidanceError } = useSelector(
    (store) => store.brandsDashboardSlice || {}
  );

  const isGuidanceLoading = Boolean(
    dashboardGuidanceLoading || (dashboardGuidanceData === null && !dashboardGuidanceError)
  );
  const guidanceData = dashboardGuidanceData;
  const brandName =
    data?.brand_name ||
    data?.company_name ||
    data?.name ||
    data?.first_name ||
    "Bare Blends";

  const { verifyEmailLoading, verifyEmailData, verifyEmailError } = useSelector(
    (store) => store.verifyEmailReducer
  );

  const {
    emailVerificationLoading,
    emailVerificationData,
    emailVerificationError,
  } = useSelector((store) => store.emailVerificationReducer);

  useEffect(() => {
    dispatch(getDashboardGuidance());

    const handleAnnouncementSync = (e) => {
      if (
        !e ||
        !e.key ||
        e.key === "rdlink_admin_announcement" ||
        e.key === "rdlink_admin_announcement_timestamp"
      ) {
        dispatch(getDashboardGuidance());
      }
    };
    window.addEventListener("storage", handleAnnouncementSync);
    return () => window.removeEventListener("storage", handleAnnouncementSync);
  }, [dispatch]);

  useEffect(() => {
    const rawUser = localStorage.getItem('userData') || localStorage.getItem(process.env.REACT_APP_USERDATA)
    if (rawUser) {
      try {
        const u = JSON.parse(rawUser)
        if (u?.subscription_exp || u?.subscriptionexp) {
          history.push("/subscription-plans")
          return
        }
      } catch (e) {}
    }
    dispatch(getBrandsSubscriptionPlans())
  }, [])

  useEffect(() => {
    if (currentPlanData && (currentPlanData.status === false || currentPlanData.subscription_exp)) {
      try {
        const rawUser = localStorage.getItem('userData') || localStorage.getItem(process.env.REACT_APP_USERDATA)
        if (rawUser) {
          const userData = JSON.parse(rawUser)
          userData.subscription_exp = true
          userData.subscriptionexp = true
          userData.active_subscription_id = null
          localStorage.setItem('userData', JSON.stringify(userData))
          if (process.env.REACT_APP_USERDATA) {
            localStorage.setItem(process.env.REACT_APP_USERDATA, JSON.stringify(userData))
          }
        }
      } catch (e) {}
      history.push("/subscription-plans")
    }
  }, [currentPlanData])

  const handleSendEmail = () => {
    setOpenModal(true);
    dispatch(sendVerifyEmailLinkAction());
  };

  const handleCloseModal = () => {
    dispatch(sendVerifyEmailLinkResetAction());
    dispatch(verifyEmailResetAction());
    setOpenModal(false);
  };

  useEffect(() => {
    if (emailVerificationData?.status) {
      const data = JSON.parse(
        localStorage.getItem(process.env.REACT_APP_USERDATA)
      );
      data.email_verified = true;
      localStorage.setItem(
        process.env.REACT_APP_USERDATA,
        JSON.stringify(data)
      );
    }
  }, [emailVerificationData]);

  return (
    <div id="dashboard-brands" className="dashboard-guidance-wrapper">
      {data && data.email_verified === false && (
        <div>
          <Alert color="danger">
            <div className="alert-body">
              <button className="btn p-0 text-danger" onClick={handleSendEmail}>
                <span>Your email is not verified. </span>
                <span className="cursor-pointer">
                  <u>Click to verify your email.</u>
                </span>
              </button>
            </div>
          </Alert>
        </div>
      )}

      {/* ─── Greeting ─── */}
      <h1 className="dashboard-greeting-title">
        Welcome back, {brandName}
      </h1>

      {/* ─── Subscription Expiry & Lifecycle Banners ─── */}
      <SubscriptionExpiryBanner
        planData={currentPlanData}
        mode="dashboard"
        onUpgradeClick={() => setUpgradeModalOpen(true)}
        onRequestInvoice={() => setInvoiceModalOpen(true)}
      />

      {/* ─── Dashboard Guidance API Failure Error State with Retry ─── */}
      {dashboardGuidanceError && !guidanceData ? (
        <Card className="border-danger shadow-sm mb-2" id="brand-dashboard-guidance-error">
          <CardBody className="p-3 text-center">
            <div
              className="rounded-circle d-inline-flex align-items-center justify-content-center mb-1"
              style={{
                width: "56px",
                height: "56px",
                backgroundColor: "rgba(234, 84, 85, 0.12)",
                color: "#ea5455"
              }}
            >
              <AlertCircle size={28} />
            </div>
            <h4 className="font-weight-bolder text-dark mb-50">
              Unable to Load Dashboard Guidance
            </h4>
            <p className="text-muted font-small-3 mb-1 mx-auto" style={{ maxWidth: "520px" }}>
              {typeof dashboardGuidanceError === "string"
                ? dashboardGuidanceError
                : dashboardGuidanceError?.message ||
                  dashboardGuidanceError?.msg ||
                  "Something went wrong while retrieving your dashboard guidance, programs, and applicants."}
            </p>
            <Button
              color="primary"
              className="d-inline-flex align-items-center px-2 py-50"
              disabled={dashboardGuidanceLoading}
              onClick={() => dispatch(getDashboardGuidance())}
              id="brand-dashboard-retry-btn"
            >
              {dashboardGuidanceLoading ? (
                <>
                  <Spinner size="sm" className="mr-50" />
                  <span>Retrying...</span>
                </>
              ) : (
                <>
                  <RefreshCw size={14} className="mr-50" />
                  <span>Retry</span>
                </>
              )}
            </Button>
          </CardBody>
        </Card>
      ) : (
        <>
          {dashboardGuidanceError && guidanceData && (
            <Alert color="danger" className="mb-2 d-flex justify-content-between align-items-center" id="brand-dashboard-guidance-sync-error">
              <div className="d-flex align-items-center">
                <AlertCircle size={18} className="mr-50 flex-shrink-0" />
                <span className="font-small-3">
                  Failed to refresh latest guidance data:{" "}
                  {typeof dashboardGuidanceError === "string"
                    ? dashboardGuidanceError
                    : dashboardGuidanceError?.message || "Connection error."}
                </span>
              </div>
              <Button
                color="danger"
                size="sm"
                outline
                className="ml-1 d-inline-flex align-items-center py-25 px-75"
                disabled={dashboardGuidanceLoading}
                onClick={() => dispatch(getDashboardGuidance())}
              >
                {dashboardGuidanceLoading ? (
                  <Spinner size="sm" />
                ) : (
                  <>
                    <RefreshCw size={12} className="mr-25" /> Retry
                  </>
                )}
              </Button>
            </Alert>
          )}

          {/* ─── Admin Announcement & Inline Notification ─── */}
          <BrandAnnouncement
            announcement={guidanceData?.announcement}
            notification={guidanceData?.notification}
            recentApplicant={guidanceData?.recent_applicants?.[0]}
            recentApplicants={guidanceData?.recent_applicants}
            isLoading={isGuidanceLoading}
          />

          {/* ─── Prioritized Next Steps (2x2 Grid) ─── */}
          <NextSteps
            steps={guidanceData?.next_steps}
            isLoading={isGuidanceLoading}
            error={dashboardGuidanceError}
            onRetry={() => dispatch(getDashboardGuidance())}
          />

          {/* ─── Two-Column Split: Your Programs & Recent Applicants ─── */}
          <Row className="match-height mb-2">
            <Col lg="6" xs="12" className="mb-2 mb-lg-0">
              <YourPrograms
                programs={guidanceData?.programs}
                isLoading={isGuidanceLoading}
                error={dashboardGuidanceError}
                onRetry={() => dispatch(getDashboardGuidance())}
              />
            </Col>
            <Col lg="6" xs="12">
              <RecentApplicants
                applicants={guidanceData?.recent_applicants}
                isLoading={isGuidanceLoading}
                error={dashboardGuidanceError}
                onRetry={() => dispatch(getDashboardGuidance())}
              />
            </Col>
          </Row>
        </>
      )}

      {/* ─── Retained Dashboard Sections ─── */}
      <Row className="match-height mb-2">
        <Col xs="12">
          <RecommendedRDs />
        </Col>
      </Row>
      <Row className="match-height mb-2">
        <Col xs="12">
          <CumulativeRatings />
        </Col>
      </Row>
      <Row className="match-height mb-2">
        <Col lg="12" sm="12">
          <RecommendedOpportunities />
        </Col>
      </Row>
      {/* ─── Membership Renewal Summary (Bottom of Dashboard - Spec Section 11) ─── */}
      <MembershipRenewalSummary
        renewal={guidanceData?.renewal}
        currentPlanData={currentPlanData}
        onUpgradeClick={() => setUpgradeModalOpen(true)}
        onRequestInvoice={() => setInvoiceModalOpen(true)}
      />
      <Modal isOpen={openModal} className="modal-dialog-centered">
        <ModalHeader>Verify Email</ModalHeader>
        <ModalBody style={{ minHeight: "50px" }}>
          {verifyEmailLoading ? (
            <div className="text-center">
              <Spinner color="primary" />
            </div>
          ) : verifyEmailData?.status ? (
            <div className="text-break">
              <p>
                <CheckCircle size={14} style={{ color: "#56B6FF" }} /> Email
                verification code has been sent to your email address.
              </p>
              <p>
                <CheckCircle size={14} style={{ color: "#56B6FF" }} /> Copy and
                paste the code below to verify your email.
              </p>
              {emailVerificationData?.status === true && (
                <Alert color="success">
                  <div className="alert-body">
                    <span>{emailVerificationData?.msg}</span>
                  </div>
                </Alert>
              )}
              {emailVerificationData?.status === false && (
                <Alert color="danger">
                  <div className="alert-body">
                    <span>{emailVerificationData?.msg}</span>
                  </div>
                </Alert>
              )}
              <Formik
                initialValues={{
                  code: "",
                }}
                validationSchema={yup.object().shape({
                  code: yup
                    .number()
                    .required("This field is required.")
                    .typeError("Should be a number"),
                })}
                onSubmit={(values) => {
                  dispatch(verifyEmailAction({ token: values.code }));
                }}
              >
                {({
                  errors,
                  handleBlur,
                  handleChange,
                  handleSubmit,
                  touched,
                  values,
                  setFieldValue,
                }) => (
                  <Form onSubmit={handleSubmit}>
                    <FormGroup>
                      <Input
                        type="number"
                        name="code"
                        id="code"
                        placeholder="Paste code here..."
                        value={values.code}
                        onBlur={handleBlur}
                        onChange={handleChange}
                        disabled={
                          emailVerificationLoading ||
                          emailVerificationData?.status
                        }
                      />
                      <div className="validation-err">
                        {touched.code && errors.code}
                      </div>
                    </FormGroup>
                    <div className="text-right">
                      <Button
                        color="danger"
                        onClick={handleCloseModal}
                        className="mr-1"
                        disabled={emailVerificationLoading}
                      >
                        Close
                      </Button>
                      <Button
                        color="primary"
                        type="submit"
                        disabled={
                          emailVerificationLoading ||
                          emailVerificationData?.status
                        }
                      >
                        {emailVerificationLoading ? (
                          <Spinner style={{ width: "16px", height: "16px" }} />
                        ) : (
                          "Verify"
                        )}
                      </Button>
                    </div>
                  </Form>
                )}
              </Formik>
            </div>
          ) : (
            <div>
              <p className="text-center">
                <AlertCircle size={50} />
              </p>
              <p>{verifyEmailData?.msg}</p>
            </div>
          )}
        </ModalBody>
      </Modal>

      {/* ─── Annual Upgrade Modal ─── */}
      <UpgradeAnnualModal
        isOpen={upgradeModalOpen}
        toggle={() => setUpgradeModalOpen(!upgradeModalOpen)}
        currentPlanData={currentPlanData}
        onUpgradeSuccess={() => dispatch(getBrandsSubscriptionPlans())}
      />

      {/* ─── Pay by Invoice Modal ─── */}
      <RequestInvoiceModal
        isOpen={invoiceModalOpen}
        toggle={() => {
          setInvoiceModalOpen(!invoiceModalOpen);
          dispatch(getBrandsSubscriptionPlans());
        }}
        user={data}
        currentPlanData={currentPlanData}
      />
    </div>
  );
};

export default BrandDashboard;
